import { useCallback, useRef } from 'react';
import { StyleSheet } from 'react-native';
import type { ViewToken } from 'react-native';
import { List } from '@banhala/ui-kit';
import type { DescriptionCardCarouselProps, DescriptionCardItem } from './types';
import { DescriptionCard } from './components/DescriptionCard';
import { isNil } from '../../../../utils';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import type { AnyObject } from '../../../../types';

const PADDING_HORIZONTAL = 12;
const GAP = 4;

type CardEntity = { item: DescriptionCardItem; logging: AnyObject };

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 1 };

const keyExtractor = (_item: CardEntity, index: number) => index.toString();

const renderItem = ({ item }: { item: CardEntity }) => (
  <DescriptionCard item={item.item} logging={item.logging} />
);

export const DescriptionCardCarousel = ({
  entity,
}: DescriptionCardCarouselProps) => {
  const displayedRef = useRef(new Set<number>());
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (index === null || !isViewable) return;
        if (displayedRef.current.has(index)) return;
        displayedRef.current.add(index);
        const cardData = item as CardEntity;
        logAnalyticsEvent({
          name: 'DISPLAY_DESCRIPTION_CARD',
          analytics: cardData.logging.analytics,
          ably_only_log: cardData.logging.ably_only_log,
        });
      });
    },
    [logAnalyticsEvent],
  );

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <List<CardEntity>
      horizontal
      data={entity}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: PADDING_HORIZONTAL,
    gap: GAP,
  },
});
